import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Waves, Sun, BookOpen, ArrowRight } from 'lucide-react';

const UCSB: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "UC Santa Barbara";

    return (
        <div className="min-h-screen bg-white font-sans text-[#003660] selection:bg-[#FEB81C] selection:text-[#003660]">
            {/* Top Bar - Navy */}
            <div className="bg-[#003660] text-white py-1.5 px-6 flex justify-end gap-6 text-[10px] font-bold uppercase tracking-widest">
                <a href="#" className="hover:text-[#FEB81C]">Parents</a>
                <a href="#" className="hover:text-[#FEB81C]">Alumni</a>
                <a href="#" className="hover:text-[#FEB81C]">Community</a>
                <a href="#" className="hover:text-[#FEB81C]">Giving</a>
            </div>

            {/* Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 border-b border-gray-100">
                <div className="max-w-[1240px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="bg-[#003660] text-[#FEB81C] font-serif font-black text-2xl h-10 w-10 flex items-center justify-center rounded-sm">{schoolName.split(' ')[0]}</div>
                        <div className="flex flex-col">
                            <span className="font-serif text-2xl font-bold text-[#003660] leading-none">{schoolName.split(' ').slice(1).join(' ')}</span>
                        </div>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#003660] text-sm uppercase tracking-wide">
                        <a href="#" className="hover:text-[#FEB81C] transition-colors hover:underline decoration-2 underline-offset-4 decoration-[#FEB81C]">Admissions</a>
                        <a href="#" className="hover:text-[#FEB81C] transition-colors hover:underline decoration-2 underline-offset-4 decoration-[#FEB81C]">Academics</a>
                        <a href="#" className="hover:text-[#FEB81C] transition-colors hover:underline decoration-2 underline-offset-4 decoration-[#FEB81C]">Research</a>
                        <a href="#" className="hover:text-[#FEB81C] transition-colors hover:underline decoration-2 underline-offset-4 decoration-[#FEB81C]">The Campus</a>
                        <a href="#" className="hover:text-[#FEB81C] transition-colors hover:underline decoration-2 underline-offset-4 decoration-[#FEB81C]">News</a>
                    </div>
                    
                    <div className="flex gap-4 items-center">
                         <Search className="cursor-pointer text-[#003660] hover:text-[#FEB81C]" size={22} />
                         <Menu className="cursor-pointer lg:hidden text-[#003660]" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - Beach / Campus Point */}
            <header className="relative h-[650px]">
                <img 
                    src="https://images.unsplash.com/photo-1562592186-59b819280fcb?q=80&w=2070&auto=format&fit=crop" 
                    alt="Campus Point" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                 {/* Gradient from bottom */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#003660]/90 via-transparent to-transparent"></div>

                <div className="absolute bottom-0 left-0 w-full p-12 md:p-20">
                     <div className="max-w-4xl mx-auto text-white">
                         <h1 className="text-5xl md:text-8xl font-serif font-bold mb-6">
                             Where land meets sea.
                         </h1>
                         <p className="text-xl md:text-2xl mb-10 font-light max-w-2xl text-blue-100">
                             We are a top-tier research university that provides a comprehensive liberal arts learning experience.
                         </p>
                         <Button className="bg-[#FEB81C] hover:bg-[#e0a010] text-[#003660] font-bold uppercase tracking-widest px-10 h-14 rounded-sm shadow-xl">
                             Explore {schoolName.split(' ')[0]}
                         </Button>
                     </div>
                </div>
            </header>

            {/* Stats / Distinction */}
            <section className="bg-[#f7f9fb] py-24 px-6 md:px-12">
                <div className="max-w-[1240px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center text-[#003660]">
                     <div className="p-8 bg-white shadow-sm rounded-lg hover:shadow-lg transition-transform hover:-translate-y-1 duration-300">
                         <Waves size={48} className="mx-auto mb-6 text-[#003660]" />
                         <h3 className="text-3xl font-serif font-bold mb-2">6</h3>
                         <p className="text-gray-500 font-bold uppercase text-xs tracking-widest">Nobel Laureates</p>
                     </div>
                      <div className="p-8 bg-white shadow-sm rounded-lg hover:shadow-lg transition-transform hover:-translate-y-1 duration-300">
                         <Sun size={48} className="mx-auto mb-6 text-[#003660]" />
                         <h3 className="text-3xl font-serif font-bold mb-2">#6</h3>
                         <p className="text-gray-500 font-bold uppercase text-xs tracking-widest">Public University (US News)</p>
                     </div>
                      <div className="p-8 bg-white shadow-sm rounded-lg hover:shadow-lg transition-transform hover:-translate-y-1 duration-300">
                         <BookOpen size={48} className="mx-auto mb-6 text-[#003660]" />
                         <h3 className="text-3xl font-serif font-bold mb-2">Top 10</h3>
                         <p className="text-gray-500 font-bold uppercase text-xs tracking-widest">Physics & Engineering</p>
                     </div>
                </div>
            </section>

             {/* News List */}
             <section className="py-24 px-6 md:px-12 bg-white">
                 <div className="max-w-[1000px] mx-auto">
                     <h2 className="text-3xl font-serif font-bold text-[#003660] mb-12 flex items-center gap-4">
                         <span className="w-12 h-1 bg-[#FEB81C]"></span>
                         Currents
                     </h2>

                     <div className="space-y-12">
                         {[
                             "Theoretical physicists propose new model for dark matter",
                             "Marine biologists discover resilient coral species",
                             "Institute for Energy Efficiency awarded major grant"
                         ].map((title, i) => (
                             <div key={i} className="flex flex-col md:flex-row gap-8 items-center border-b border-gray-100 pb-12 group cursor-pointer last:border-0 last:pb-0">
                                 <div className="w-full md:w-1/3 h-48 bg-gray-100 rounded-sm overflow-hidden">
                                     <img src={`https://source.unsplash.com/random/400x300?sig=${i+90}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" alt="News"/>
                                 </div>
                                 <div className="flex-1">
                                     <span className="text-[#FEB81C] font-bold uppercase text-xs tracking-widest mb-2 block">Research</span>
                                     <h3 className="text-2xl font-serif font-bold text-[#003660] group-hover:text-[#00528a] transition-colors mb-4">{title}</h3>
                                     <p className="text-gray-600 mb-4">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</p>
                                      <div className="flex items-center gap-2 text-[#003660] font-bold text-sm uppercase tracking-widest group-hover:gap-3 transition-all">
                                         Read Story <ArrowRight size={14}/>
                                     </div>
                                 </div>
                             </div>
                         ))}
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#003660] text-gray-400 py-16 px-6 md:px-12">
                  <div className="max-w-[1240px] mx-auto flex flex-col md:flex-row justify-between gap-12 font-sans text-sm">
                      <div className="max-w-xs">
                           <span className="font-serif font-bold text-2xl text-white mb-6 block uppercase tracking-wide">{schoolName}</span>
                          <p className="mb-6">
                              Santa Barbara, CA 93106 <br/>
                              805-893-8000
                          </p>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 font-bold uppercase text-xs tracking-widest text-[#FEB81C]">
                          <ul className="space-y-4 hover:[&_a]:text-white">
                              <li><a href="#">Terms of Use</a></li>
                              <li><a href="#">Privacy</a></li>
                              <li><a href="#">Accessibility</a></li>
                          </ul>
                          <ul className="space-y-4 hover:[&_a]:text-white">
                               <li><a href="#">Directions</a></li>
                              <li><a href="#">Contact</a></li>
                              <li><a href="#">Visit</a></li>
                          </ul>
                      </div>
                  </div>
                  <div className="max-w-[1240px] mx-auto mt-16 text-center text-xs text-blue-300/50 border-t border-blue-900 pt-8">
                      © 2026 The Regents of the University of California
                  </div>
             </footer>
        </div>
    );
};

export default UCSB;
